<template>
    <div class="mx-auto max-w-screen-lg">
        <div class="flex flex-col mb-16" v-if="businesses.length > 0">
            <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-2">Account Data</h3>

            <dl class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div class="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6">
                    <dt class="text-sm font-medium text-gray-500 truncate">My JournoLink account will be primarily used to...</dt>
                    <dd class="mt-3 text-2xl font-semibold text-gray-900">
                        {{ businesses[0]?.profile?.reason ?? 'Not Provided' }}
                    </dd>
                </div>

                <div class="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6">
                    <dt class="text-sm font-medium text-gray-500 truncate">I'm going to be using JournoLink for...</dt>
                    <dd class="mt-3 text-2xl font-semibold text-gray-900">
                        {{ businesses[0].profile?.purpose ?? 'Not Provided' }}
                    </dd>
                </div>
            </dl>
        </div>
        <div class="my-16 flex flex-col" v-if="team.length > 0">
            <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-4">Team Members</h3>

            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border border-gray-300 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-100">
                            <tr>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th scope="col" class="relative px-6 py-3">
                                    <span class="sr-only">View</span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(teamMember, index) in team" :key="teamMember.id"
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {{ teamMember.profile?.name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ teamMember.email }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <router-link :to="{ name: 'users.detail', params: { userId: teamMember.id }}">
                                        <EyeIcon class="h-6 w-6 text-gray-400 hover:text-gray-800 cursor-pointer"/>
                                    </router-link>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        <div class="my-16 flex flex-col" v-if="businesses.length > 0">
            <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-4">Businesses</h3>
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border border-gray-300 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-100">
                            <tr>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Business
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created
                                </th>
                                <th scope="col" class="relative px-6 py-3">
                                    <span class="sr-only">Edit</span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(business, index) in businesses" :key="business.id"
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'">
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {{ business.name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ formatDateShort(business.created_at) }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <router-link
                                        :to="{ name: 'business.show', params: { businessId: business.id, accountId: account.id }}">
                                        <EyeIcon class="h-6 w-6 text-gray-400 hover:text-gray-800 cursor-pointer"/>
                                    </router-link>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        <div class="my-16 flex flex-col">
            <h3 class="text-2xl tracking-widest uppercase font-bold text-gray-700 mb-4">Account Options</h3>
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div>
                        <button
                            v-if="!flags['skip_approvals']"
                            type="button"
                            @click="toggleFlag('skip_approvals')"
                            class="inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md bg-green-200 hover:bg-green-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 mr-4">
                            <DocumentCheckIcon class="h-5 w-5 mr-2"/>
                            Trust All Press Releases
                        </button>
                        <button
                            v-if="flags['skip_approvals']"
                            type="button"
                            @click="toggleFlag('skip_approvals')"
                            class="inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md bg-red-200 hover:bg-red-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 mr-4">
                            <DocumentMagnifyingGlassIcon class="h-5 w-5 mr-2"/>
                            Mark Press Releases for Review
                        </button>

                        <button
                            v-if="!flags['container_owner']"
                            type="button"
                            @click="toggleFlag('container_owner')"
                            class="inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md bg-yellow-200 hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 mr-4">
                            <BuildingLibraryIcon class="h-5 w-5 mr-2"/>
                            Mark Account as Enterprise Owner
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {ref, onMounted} from "vue"
import {EyeIcon, DocumentCheckIcon, DocumentMagnifyingGlassIcon, BuildingLibraryIcon} from "@heroicons/vue/24/outline"
import {useBus} from "../../../../app";

export default {
    name: "Account",
    components: {
        EyeIcon,
        DocumentCheckIcon,
        DocumentMagnifyingGlassIcon,
        BuildingLibraryIcon
    },
    props: {
        user: {
            type: Object,
            required: true,
        }
    },
    setup(props) {
        const bus = useBus()

        const account = ref(null)
        const businesses = ref([])
        const team = ref([])
        const flags = ref({})

        const getAccount = async () => {
            await axios.get(route('accounts.show', {account: props.user?.account_id}))
                .then((response) => {
                    account.value = response.data.account
                    businesses.value = response.data.account.businesses
                    team.value = response.data.account.users
                    flags.value = response.data.account.flags ?? []
                })
        }

        const saveAccountFlags = async () => {
            console.log(flags.value)
            await axios.post(route('accounts.update', account.value.id), {
                flags: flags.value,
            }).then(response => {
                account.value = response.data.account
                flags.value = response.data.account.flags

                bus.$emit('alert.success', 'Update Success')
            }).catch(response => {
                bus.$emit('alert.error', 'Update Failed')
            })
        }

        const toggleFlag = async (flag) => {
            if (Object.keys(flags.value).length === 0) {
                flags.value[flag] = 1
            } else {
                flags.value[flag] = !flags.value[flag]
            }

            await saveAccountFlags()
        }

        onMounted(() => {
            getAccount()
            window.location.hash = 'account'
        })

        return {
            account,
            businesses,
            team,
            flags,
            toggleFlag
        }
    },
}
</script>

<style scoped>

</style>
